import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableFooter,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery,
  SelectChangeEvent,
} from '@mui/material';
import FarmMenu from '../Components/FarmMenu';
import SellIcon from '@mui/icons-material/Sell';
import FarmContants from '../Model/FarmConstants';
import TenantTable from '../Components/TenantTable/TenantTable';
import CommonTag from '../../../Common/Tag/CommonTag';
import IndexingSearchMachine from '../Components/IndexingSearchMachine';
import FarmHeaderToolbar from '../Components/FarmHeaderToolbar';
import { HighAvailabilityCell } from '../Components/HighAvailabilityCell';
import { FarmStateCell } from '../Components/FarmStateCell';
import { ThemedLaunchIcon } from '../../../../Common/Components/Icons';
import { farmService } from '../FarmService';
import { globalService } from '../../UIContainer/Global/GlobalService';
import { storageAccountService } from '../../StorageAccount/StorageAccountService';
import { azureSearchServiceService } from '../../SearchService/AzureSearchServiceService';
import { sqlServerService } from '../../SqlServer/SqlServerService';
import { Farm, FarmTopologies } from '../Model/FarmModel';
import { StorageAccount } from '../../StorageAccount/Model/StorageAccountModel';
import { AzureSearchService } from '../../SearchService/Model/AzureSearchServiceModel';
import { SqlServer } from '../../SqlServer/Model/SqlServerModel';
import { TagDetails } from '../../../Common/CommonModel';
import { customerService } from '../../Customer/CustomerService';
import { Customer } from '../../Customer/Model/CustomerModel';
import { showToast } from '../../../../lib/toast';
import { TableSkeleton } from '../Components/TableSkeleton';

// Extracted Row Component for Performance
const FarmRow = React.memo(
  ({
    farm,
    handleRowExpand,
    setSelectedFarm,
    setShowTags,
  }: {
    farm: Farm;
    handleRowExpand: (farm: Farm) => void;
    setSelectedFarm: (id: string) => void;
    setShowTags: (show: boolean) => void;
  }) => {
    return (
      <TableRow
        hover
        sx={{
          '& td': { borderBottom: 'none', py: 1.5 },
          transition: 'background-color 0.2s ease',
          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.02) !important' },
        }}
      >
        <TableCell sx={{ width: 48 }}>
          <IconButton onClick={() => handleRowExpand(farm)} size="small">
            <ThemedLaunchIcon />
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>
          {farm.Id}
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{farm.DisplayName}</TableCell>
        <TableCell sx={{ color: 'text.secondary' }}>{farm.GroupName}</TableCell>
        <FarmStateCell state={farm.State} />
        <HighAvailabilityCell isHighAvailability={farm.IsHighAvailability} />
        <TableCell sx={{ color: 'text.secondary' }}>
          {farm.FarmRegion}
        </TableCell>
        <TableCell>
          <IndexingSearchMachine farmId={farm.Id} />
        </TableCell>
        <TableCell>
          <Tooltip title="Edit Tags">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedFarm(farm.Id.toString());
                setShowTags(true);
              }}
            >
              <SellIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          <FarmMenu FarmId={farm.Id.toString()} />
        </TableCell>
      </TableRow>
    );
  },
);

const RowsPerPage = 10;

export default function FarmTable() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [farms, setFarms] = useState<Farm[]>([]);
  const [farmTopologies, setFarmTopologies] = useState<FarmTopologies[]>([]);
  const [storageAccounts, setStorageAccounts] = useState<StorageAccount[]>([]);
  const [azureSearchServices, setAzureSearchServices] = useState<
    AzureSearchService[]
  >([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [sqlServers, setSqlServers] = useState<SqlServer[]>([]);
  const [farmTagDetails, setFarmTagDetails] = useState<TagDetails[]>([]);
  const [tagOptions, setTagOptions] = useState<string[]>([]);

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [openFarm, setOpenFarm] = useState<Farm | null>(null);
  const [dialogState, setDialogState] = useState(false);
  const [showTags, setShowTags] = useState(false);

  const [farmType, setFarmType] = useState('ProArc Online');
  const [farmTags, setFarmTags] = useState<string[]>([]);
  const [selectedFarmTags, setSelectedFarmTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFarm, setSelectedFarm] = useState('');

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [
        farmsRes,
        topologies,
        storage,
        azureSearch,
        sql,
        tags,
        tagDetails,
        customerList,
      ] = await Promise.all([
        farmService.getFarms(),
        farmService.getFarmTopologies(),
        storageAccountService.getstorageAccounts(),
        azureSearchServiceService.getAzureSearchServices(),
        sqlServerService.getSqlServers(),
        globalService.getTags(),
        globalService.getTagDetailsByObjectType('farm'),
        customerService.getCustomers(),
      ]);

      const farmItems = farmsRes.Items || [];
      setFarms(farmItems);
      setFarmTopologies(topologies.Items || []);
      setCustomers(customerList.Items || []);
      setStorageAccounts(storage.Items || []);
      setAzureSearchServices(azureSearch.Items || []);
      setSqlServers(sql.Items || []);
      setTagOptions(tags.Items.map((x) => x.TagName));
      setFarmTagDetails(tagDetails.Items);
      const uniqueTagNames = [
        ...new Set(tagDetails.Items.map((x) => x.TagName)),
      ];
      setFarmTags(uniqueTagNames);
    } catch (error) {
      showToast({
        title: `Error loading data: ${error}`,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const customerMap = useMemo(() => {
    const map = new Map<string, string>();
    customers.forEach((c) => {
      if (c.Id && c.CustomerName) map.set(c.Id, c.CustomerName.toLowerCase());
    });
    return map;
  }, [customers]);

  const filteredFarms = useMemo(() => {
    let result = farms;

    if (farmType) {
      result = result.filter((f) => f.Type === farmType);
    }

    if (selectedFarmTags.length) {
      const matchingFarmIds = farmTagDetails
        .filter((x) => selectedFarmTags.includes(x.TagName))
        .map((x) => x.ObjectId);
      result = result.filter((f) => matchingFarmIds.includes(f.Id.toString()));
    }

    if (searchQuery) {
      const lower = searchQuery.toLowerCase();

      result = result.filter((farm) =>
        farm.Tenants?.some((tenant) => {
          const customerName = customerMap.get(tenant.Id) || '';
          return (
            tenant.ApplicationUrl.toLowerCase().includes(lower) ||
            tenant.Id.toLowerCase().includes(lower) ||
            customerName.includes(lower)
          );
        }),
      );
    }

    return result;
  }, [
    farms,
    farmType,
    selectedFarmTags,
    searchQuery,
    farmTagDetails,
    customerMap,
  ]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, farmType, selectedFarmTags]);

  const paginatedFarms = useMemo(() => {
    return filteredFarms.slice(page * RowsPerPage, (page + 1) * RowsPerPage);
  }, [filteredFarms, page]);

  const updateSSlBindings = async () => {
    try {
      const confirm = window.confirm('You want to update ssl bindings?');
      if (!confirm) return;
      await farmService.updateSSlBindings();
      showToast({
        type: 'success',
        title: FarmContants.QueuedSSLRunbook,
      });
    } catch (error) {
      console.log(error);
      showToast({
        type: 'error',
        title: 'Failed to update SSL bindings',
      });
    }
  };

  const handleRowExpand = (farm: Farm) => {
    setOpenFarm(farm);
    setDialogState(true);
  };

  const handleSaveTags = async (tags: string[]) => {
    try {
      setLoading(true);
      const tagDetails: TagDetails[] = tags.map((tag) => ({
        ObjectId: selectedFarm,
        ObjectType: 'farm',
        TagName: tag,
      }));
      await globalService.saveTagDetails(selectedFarm, tagDetails);
      showToast({
        type: 'success',
        title: `Tags saved for farm ${selectedFarm}`,
      });
      await fetchInitialData();
      setShowTags(false);
    } catch (err) {
      showToast({
        title: 'Error saving tags',
        type: 'error',
      });
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const tenantTableData = useMemo(() => {
    if (!openFarm) return null;
    return {
      storage: storageAccounts.filter(
        (sa) =>
          sa.AzureRegion.toLowerCase() === openFarm.FarmRegion.toLowerCase(),
      ),
      search: azureSearchServices.filter(
        (sa) =>
          sa.AzureRegion.toLowerCase() === openFarm.FarmRegion.toLowerCase(),
      ),
      sql: sqlServers.filter(
        (sql) => sql.Region.toLowerCase() === openFarm.FarmRegion.toLowerCase(),
      ),
    };
  }, [openFarm, storageAccounts, azureSearchServices, sqlServers]);

  return (
    <>
      <FarmHeaderToolbar
        Type={farmType}
        handleChange={(e: SelectChangeEvent) => setFarmType(e.target.value)}
        farmtopologies={farmTopologies}
        farmTags={farmTags}
        selectedFarmTags={selectedFarmTags}
        onSelectedFarmTag={setSelectedFarmTags}
        updateSSlBindings={updateSSlBindings}
        onFilterRequest={(e) => setSearchQuery(e.target.value)}
      />

      <Paper
        elevation={0}
        sx={{
          overflowX: 'auto',
          marginBottom: 2,
          bgcolor: 'background.paper',
          backdropFilter: 'blur(32px)',
          backgroundColor: 'rgba(17, 20, 23, 0.6)',
          borderRadius: '1rem',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
        }}
      >
        <Box sx={{ minWidth: isMobile ? '600px' : 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ borderBottom: 'none', py: 2 }} />
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    py: 2,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                  }}
                >
                  Id
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    py: 2,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                  }}
                >
                  Display Name
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    py: 2,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                  }}
                >
                  Group
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    py: 2,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                  }}
                >
                  State
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    py: 2,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                  }}
                >
                  HA
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    py: 2,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                  }}
                >
                  Region
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    py: 2,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                  }}
                >
                  Indexing
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    py: 2,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                  }}
                >
                  Tags
                </TableCell>
                <TableCell sx={{ borderBottom: 'none', py: 2 }} />
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableSkeleton rows={RowsPerPage} columns={10} />
              ) : (
                <>
                  {showTags && !loading && (
                    <CommonTag
                      objectType="Farm"
                      saveTags={handleSaveTags}
                      objectId={selectedFarm}
                      options={tagOptions}
                      setShowTags={setShowTags}
                    />
                  )}
                  {paginatedFarms.map((farm: Farm) => (
                    <FarmRow
                      key={farm.Id}
                      farm={farm}
                      handleRowExpand={handleRowExpand}
                      setSelectedFarm={setSelectedFarm}
                      setShowTags={setShowTags}
                    />
                  ))}
                </>
              )}
            </TableBody>

            {!loading && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[RowsPerPage]}
                    count={filteredFarms.length}
                    rowsPerPage={RowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    sx={{ borderBottom: 'none' }}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </Box>
      </Paper>

      {dialogState && openFarm && tenantTableData && (
        <TenantTable
          farm={openFarm}
          storageaccounts={tenantTableData.storage}
          azureSearchService={tenantTableData.search}
          sqlServers={tenantTableData.sql}
          replicaServers={sqlServers}
          searchQuery={searchQuery}
          setDialogState={setDialogState}
          dialogState={dialogState}
        />
      )}
    </>
  );
}
